import {
  GRID_COLS,
  GRID_ROWS,
  BLOCK_COLORS,
  BLOCK_COLOR_LIST,
  DIFFICULTY_CONFIG,
  BLOCK_SIZES,
} from './constants';
import { isLevelSolvable, solve } from './solver';

/** Fingerprint a level layout (ignores colors/ids, only positions matter) */
function getLayoutFingerprint(blocks) {
  return blocks
    .map(b => `${b.x},${b.y},${b.width},${b.height},${b.isPrisoner ? 1 : 0}`)
    .sort()
    .join('|');
}

/** Module-level seen patterns cache (persists across calls within session) */
const seenPatterns = new Set();

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomChoice(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function createGrid() {
  const grid = [];
  for (let y = 0; y < GRID_ROWS; y++) {
    grid.push(new Array(GRID_COLS).fill(null));
  }
  return grid;
}

function canPlaceBlock(grid, x, y, width, height) {
  if (x < 0 || y < 0 || x + width > GRID_COLS || y + height > GRID_ROWS) {
    return false;
  }
  for (let dy = 0; dy < height; dy++) {
    for (let dx = 0; dx < width; dx++) {
      if (grid[y + dy][x + dx] !== null) {
        return false;
      }
    }
  }
  return true;
}

function placeBlock(grid, block) {
  for (let dy = 0; dy < block.height; dy++) {
    for (let dx = 0; dx < block.width; dx++) {
      grid[block.y + dy][block.x + dx] = block.id;
    }
  }
}

function getGenerationBudget(difficulty) {
  switch (difficulty) {
    case 'medium':
      return { maxAttempts: 60, maxDepth: 14, maxStates: 20000 };
    case 'hard':
      return { maxAttempts: 90, maxDepth: 16, maxStates: 30000 };
    case 'easy':
    default:
      return { maxAttempts: 80, maxDepth: 10, maxStates: 8000 };
  }
}

function verifyLevel(blocks, budget) {
  return isLevelSolvable(blocks, {
    maxDepth: Math.max(budget.maxDepth + 20, 40),
    maxStates: Math.max(budget.maxStates * 3, 50000),
  });
}

function yieldIntervalForDifficulty(difficulty) {
  switch (difficulty) {
    case 'hard':
      return 1;
    case 'medium':
      return 4;
    default:
      return 8;
  }
}

function yieldToMain() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: 32 });
      return;
    }
    setTimeout(resolve, 0);
  });
}

function acceptGeneratedLevel(result, budget) {
  if (!result) return null;

  const verified = verifyLevel(result.blocks, budget);
  if (!verified.solvable) return null;

  const fingerprint = getLayoutFingerprint(result.blocks);
  if (seenPatterns.has(fingerprint)) return null;
  seenPatterns.add(fingerprint);

  return {
    ...result,
    optimalMoves: verified.minMoves,
  };
}

function finalizeWithFallback(difficulty, config, budget) {
  const fallbackOrder = difficulty === 'easy'
    ? ['easy']
    : difficulty === 'medium'
      ? ['medium', 'easy']
      : ['hard', 'medium', 'easy'];

  for (const fallbackDifficulty of fallbackOrder) {
    const fallback = generateFallbackLevel(fallbackDifficulty);
    const verified = verifyLevel(fallback.blocks, budget);
    if (!verified.solvable) continue;

    const fingerprint = getLayoutFingerprint(fallback.blocks);
    seenPatterns.add(fingerprint);

    return {
      ...fallback,
      optimalMoves: verified.minMoves,
      difficulty: fallbackDifficulty,
    };
  }

  // Guaranteed solvable easy template (last resort).
  const easyFallback = generateFallbackLevel('easy');
  const verified = verifyLevel(easyFallback.blocks, budget);
  return {
    ...easyFallback,
    optimalMoves: verified.solvable ? verified.minMoves : 3,
    difficulty: 'easy',
  };
}

function runGenerationAttempts(difficulty, maxAttempts) {
  const config = DIFFICULTY_CONFIG[difficulty];
  const budget = getGenerationBudget(difficulty);
  const attemptLimit = Math.min(maxAttempts, budget.maxAttempts);

  for (let attempt = 0; attempt < attemptLimit; attempt++) {
    const accepted = acceptGeneratedLevel(tryGenerateLevel(config, difficulty, budget), budget);
    if (accepted) return accepted;
  }

  const relaxedConfig = {
    ...config,
    minBlocks: Math.max(3, config.minBlocks - 1),
    maxBlocks: config.maxBlocks + 1,
    minMoves: Math.max(2, config.minMoves - 1),
    maxMoves: config.maxMoves + 2,
  };

  for (let attempt = 0; attempt < 80; attempt++) {
    const accepted = acceptGeneratedLevel(tryGenerateLevel(relaxedConfig, difficulty, budget), budget);
    if (accepted) return accepted;
  }

  return finalizeWithFallback(difficulty, config, budget);
}

function generateLevel(difficulty = 'easy', maxAttempts = 100) {
  const level = runGenerationAttempts(difficulty, maxAttempts);
  return level;
}

async function generateLevelAsync(difficulty = 'easy', maxAttempts = 100) {
  const config = DIFFICULTY_CONFIG[difficulty];
  const budget = getGenerationBudget(difficulty);
  const attemptLimit = Math.min(maxAttempts, budget.maxAttempts);
  const yieldEvery = yieldIntervalForDifficulty(difficulty);

  for (let attempt = 0; attempt < attemptLimit; attempt++) {
    if (attempt > 0 && attempt % yieldEvery === 0) {
      await yieldToMain();
    }
    const accepted = acceptGeneratedLevel(tryGenerateLevel(config, difficulty, budget), budget);
    if (accepted) return accepted;
  }

  const relaxedConfig = {
    ...config,
    minBlocks: Math.max(3, config.minBlocks - 1),
    maxBlocks: config.maxBlocks + 1,
    minMoves: Math.max(2, config.minMoves - 1),
    maxMoves: config.maxMoves + 2,
  };

  for (let attempt = 0; attempt < 80; attempt++) {
    if (attempt > 0 && attempt % yieldEvery === 0) {
      await yieldToMain();
    }
    const accepted = acceptGeneratedLevel(tryGenerateLevel(relaxedConfig, difficulty, budget), budget);
    if (accepted) return accepted;
  }

  return finalizeWithFallback(difficulty, config, budget);
}

function tryGenerateLevel(config, difficulty, budget) {
  const grid = createGrid();
  const blocks = [];
  let blockIdCounter = 0;
  const usedColors = new Set();
  
  const prisonerRow = randomInt(0, GRID_ROWS - 1);
  const prisonerStartCol = randomInt(0, 3);
  
  const prisoner = {
    id: `block_${blockIdCounter++}`,
    x: prisonerStartCol,
    y: prisonerRow,
    width: 2,
    height: 1,
    orientation: 'H',
    color: BLOCK_COLORS.prisoner,
    isPrisoner: true,
  };
  
  if (!canPlaceBlock(grid, prisoner.x, prisoner.y, prisoner.width, prisoner.height)) {
    return null;
  }
  
  placeBlock(grid, prisoner);
  blocks.push(prisoner);
  
  const numBlocks = randomInt(config.minBlocks, config.maxBlocks);
  const availableColors = shuffle([...BLOCK_COLOR_LIST]);
  let colorIndex = 0;
  
  const blockingPositions = [];
  for (let col = prisoner.x + prisoner.width; col < GRID_COLS; col++) {
    blockingPositions.push({ x: col, y: prisonerRow });
  }
  
  let blocksPlaced = 0;
  let placementAttempts = 0;
  const maxPlacementAttempts = 200;
  
  while (blocksPlaced < numBlocks && placementAttempts < maxPlacementAttempts) {
    placementAttempts++;
    
    const blockSize = randomChoice(BLOCK_SIZES);
    const { width, height, orientation } = blockSize;
    
    let x, y;
    
    if (blocksPlaced < 2 && blockingPositions.length > 0 && orientation === 'V' && height >= 2) {
      const blockingPos = randomChoice(blockingPositions);
      x = blockingPos.x;
      y = Math.max(0, blockingPos.y - randomInt(0, height - 1));
      y = Math.min(y, GRID_ROWS - height);
    } else {
      x = randomInt(0, GRID_COLS - width);
      y = randomInt(0, GRID_ROWS - height);
    }
    
    if (canPlaceBlock(grid, x, y, width, height)) {
      const color = availableColors[colorIndex % availableColors.length];
      colorIndex++;
      
      const block = {
        id: `block_${blockIdCounter++}`,
        x,
        y,
        width,
        height,
        orientation,
        color,
        isPrisoner: false,
      };
      
      placeBlock(grid, block);
      blocks.push(block);
      blocksPlaced++;
    }
  }
  
  if (blocksPlaced < config.minBlocks) {
    return null;
  }
  
  const solution = solve(blocks, budget.maxDepth, budget.maxStates);
  
  if (!solution.solvable) {
    return null;
  }
  
  if (solution.minMoves < config.minMoves || solution.minMoves > config.maxMoves) {
    return null;
  }
  
  return {
    blocks,
    optimalMoves: solution.minMoves,
    difficulty,
  };
}

function generateFallbackLevel(difficulty) {
  const blocks = [];
  let blockIdCounter = 0;
  
  const prisoner = {
    id: `block_${blockIdCounter++}`,
    x: 0,
    y: 2,
    width: 2,
    height: 1,
    orientation: 'H',
    color: BLOCK_COLORS.prisoner,
    isPrisoner: true,
  };
  blocks.push(prisoner);
  
  if (difficulty === 'easy') {
    // Easy: 4 blocks total (prisoner + 3 other blocks)
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 4,
      y: 1,
      width: 1,
      height: 3,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[0],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 5,
      y: 0,
      width: 1,
      height: 2,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[1],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 6,
      y: 2,
      width: 1,
      height: 2,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[2],
      isPrisoner: false,
    });
  } else if (difficulty === 'medium') {
    // Verified solvable medium fallback (prisoner + 5 blocks).
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 3,
      y: 1,
      width: 1,
      height: 3,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[0],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 5,
      y: 0,
      width: 1,
      height: 2,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[1],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 6,
      y: 1,
      width: 1,
      height: 2,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[2],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 4,
      y: 4,
      width: 2,
      height: 1,
      orientation: 'H',
      color: BLOCK_COLOR_LIST[3],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 0,
      y: 4,
      width: 2,
      height: 1,
      orientation: 'H',
      color: BLOCK_COLOR_LIST[4],
      isPrisoner: false,
    });
  } else {
    // Verified solvable hard fallback (prisoner + 7 blocks).
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 2,
      y: 0,
      width: 1,
      height: 2,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[0],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 4,
      y: 0,
      width: 1,
      height: 3,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[1],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 5,
      y: 0,
      width: 1,
      height: 2,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[2],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 6,
      y: 0,
      width: 1,
      height: 2,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[3],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 0,
      y: 3,
      width: 2,
      height: 1,
      orientation: 'H',
      color: BLOCK_COLOR_LIST[4],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 0,
      y: 4,
      width: 3,
      height: 1,
      orientation: 'H',
      color: BLOCK_COLOR_LIST[5],
      isPrisoner: false,
    });
    blocks.push({
      id: `block_${blockIdCounter++}`,
      x: 6,
      y: 4,
      width: 1,
      height: 2,
      orientation: 'V',
      color: BLOCK_COLOR_LIST[6],
      isPrisoner: false,
    });
  }

  const budget = getGenerationBudget(difficulty);
  const verified = verifyLevel(blocks, budget);

  return {
    blocks,
    optimalMoves: verified.solvable ? verified.minMoves : 3,
    difficulty,
  };
}

export { generateLevel, generateLevelAsync };
